const CSV_IMPORT_ERROR_FIELDS = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "selectedDocumentUuids"
] as const;

type CsvImportErrorField = typeof CSV_IMPORT_ERROR_FIELDS[number];
type CsvImportErrorEntry = [rowIndex: number, message: string];

export type CsvImportErrors = Partial<Record<CsvImportErrorField, CsvImportErrorEntry[]>>;

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const isCsvImportErrorField = (field: string): field is CsvImportErrorField => {
    return CSV_IMPORT_ERROR_FIELDS.some((allowedField: CsvImportErrorField): boolean => allowedField === field);
};

const isCsvImportErrorEntry = (value: unknown): value is CsvImportErrorEntry => {
    return Array.isArray(value)
        && value.length === 2
        && Number.isInteger(value[0])
        && value[0] >= 0
        && typeof value[1] === "string";
};

export function parseCsvImportErrors(responseBody: unknown): CsvImportErrors | null {
    if (!isRecord(responseBody) || !isRecord(responseBody.error)) {
        return null;
    }

    const fieldEntries = Object.entries(responseBody.error);
    if (fieldEntries.length === 0) {
        return null;
    }

    const parsedErrors: CsvImportErrors = {};
    for (const [field, errors] of fieldEntries) {
        if (!isCsvImportErrorField(field) || !Array.isArray(errors) || !errors.every(isCsvImportErrorEntry)) {
            return null;
        }

        parsedErrors[field] = errors.map(([rowIndex, message]): CsvImportErrorEntry => [rowIndex, message]);
    }

    return parsedErrors;
}

export function getCsvImportErrorMessages(errors: CsvImportErrors): string[] {
    return CSV_IMPORT_ERROR_FIELDS.flatMap((field: CsvImportErrorField): string[] => {
        return (errors[field] || []).map(([, message]: CsvImportErrorEntry): string => message);
    });
}
